import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';

import { Grid } from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBack';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import DashboardLayout from '../DashboardLayout';
import { Loader } from '../Loading';
import SubscriptionCard from './SubscriptionCard';
import ChangeSubscription from './ChangeSubscription';
import GenericNotFound from '../GenericNotFound';
import Button from '../Button';
import SubscriptionPauseResume from './SubscriptionPauseResume';
import SubscriptionPaymentInfo from './SubscriptionPaymentInfo';
import { withNavigation } from '../../actions/navigation';
import ChangeSubscriptionPlan from '../plans/ChangeSubscriptionPlan';
import PageHeader from '../PageHeader';
import SetCurrentProduct from '../../containers/SetCurrentProduct';
import { PERIODICITY } from '../../actions/products';
import RootRef from '@material-ui/core/RootRef';

/**
 * Update Subscription Page component, containing
 * Overview of the subscription (Subscription Card) with "change subscription" form
 */

const styles = theme => ({
  colorDefault: {
    color: '#797979'
  },
  leftIcon: {
    marginRight: theme.spacing.unit,
  },
  topContainer: {
    display:'flex', 
    direction:'row', 
    justifyContent:'space-between', 
    marginBottom: 68
  },
  subTitle: {
    marginBottom: 14,
  },
  coloredBackgroundDiv: {
    backgroundColor: "#ECF1FD",
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    zIndex: "-10"
  },
  subscriptionCard:{
    minWidth: 337,
  }
});

class UpdateSubscriptionPage extends Component {

  constructor(props) {
    super(props);
    this.coloredBackgroundDivRef = React.createRef();
    this.state = {
      switchOnMonthlyPlans: this.props.planTargetPeriodicity === PERIODICITY.MONTHLY ? true : false,
      switchOnMonthlySubscriptionPlan: this.props.currentSubscriptionPlanPeriodicity === PERIODICITY.MONTHLY ? true : false,
      leftBackgroundWidth: 0,
    };
    this.handleSwitchOnSubscriptionPlanPeriod = this.handleSwitchOnSubscriptionPlanPeriod.bind(this);
    this.handleSwitchOnPeriod = this.handleSwitchOnPeriod.bind(this);
  }
  
  componentDidMount() {
    window.addEventListener('resize', () => this.updateDimensions());
    this.updateDimensions();
  }
  componentWillUnmount() {
    window.removeEventListener('resize', () => this.updateDimensions());
  }
  updateDimensions() {
    if (this.coloredBackgroundDivRef.current) {
      this.setState({ leftBackgroundWidth: this.coloredBackgroundDivRef.current.offsetLeft + this.coloredBackgroundDivRef.current.clientWidth + 8});
    }
  }

  /**
   * Handle the chosen periodicity related to SubscriptionPlan and update the subscription  
   * @param {*} chosenPeriodicity 
   */
  handleSwitchOnSubscriptionPlanPeriod (targetPlan, licencesCount) {
    if (targetPlan.periodicity === PERIODICITY.MONTHLY) {
      this.setState({switchOnMonthlySubscriptionPlan: true});
    } 
    if (targetPlan.periodicity === PERIODICITY.YEARLY) {
      this.setState({switchOnMonthlySubscriptionPlan: false});
    }
    this.props.handlePlanChange({plan: targetPlan, licencesCount });
  }

  /**
   * Handle the chosen periodicity related to Others plans displayed  
   * @param {*} chosenPeriodicity 
   */
  handleSwitchOnPeriod (chosenPeriodicity) {
    if (chosenPeriodicity === PERIODICITY.MONTHLY) {
      this.setState({switchOnMonthlyPlans: true});
    } 
    if (chosenPeriodicity === PERIODICITY.YEARLY) {
      this.setState({switchOnMonthlyPlans: false});
    }
    this.props.handlePlanTargetPeriodicity(chosenPeriodicity);
  }

  render() {
    const {
      intl,
      subscription,
      product,
      cancelSubscription,
      reactivateSubscription,
      handlePaymentInfo,
      navigateToSubscription,
      handlePlanChange,
      handleCurrentPlanUpdate,
      planTargetPeriodicity,
      classes,
    } = this.props;

    const {switchOnMonthlyPlans}= this.state;
        
    const currentPlanId = subscription && subscription.plan && subscription.plan.id;
    const otherPlans = (product 
      && product.plans 
      && product.plans.filter( plan => 
        plan.id !== currentPlanId )) || [];

    const isExistingYearlyPlan = product && (product.plans.filter(plan => plan.periodicity === PERIODICITY.YEARLY).length >= 1);
    const isExistingMonthlyPlan = product && (product.plans.filter(plan => plan.periodicity === PERIODICITY.MONTHLY).length >= 1);
    const isBothPeriodicityAvailable = isExistingMonthlyPlan && isExistingYearlyPlan;

    if (!subscription) {
      return <DashboardLayout
        title={
          intl.messages['subscription.page.title']
        }>
        <Loader />
      </DashboardLayout>;
    }

    if (subscription.notFound) {
      return (
        <DashboardLayout>
          <GenericNotFound
            message={
              <FormattedMessage id='subscription.notFound' />
            }
          />
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout
        specificSelectedColor={(otherPlans && otherPlans.length !== 0)? true : false}
        title={
          product
            ? intl.formatMessage(
              {
                id: 'subscription.page.titleWithProduct'
              },
              {
                productName: product.name
              }
            )
            : intl.messages['subscription.page.title']
        }>
        {
          product && <SetCurrentProduct product={product} />
        }
        {subscription && subscription.plan // FIXME: not a good way to know whether it's loaded
          ? (
            
            <div>
              <div 
                className={(otherPlans && otherPlans.length !== 0) ? classes.coloredBackgroundDiv : classes.colorDefault} 
                style={{width: (otherPlans && otherPlans.length !== 0) ? this.state.leftBackgroundWidth : '100%'}}/>
              <PageHeader actions={
                <div>
                  {
                    !subscription.locked && subscription.hasPaymentInfo &&
                      <SubscriptionPaymentInfo
                        handlePaymentInfo={handlePaymentInfo}
                        subscription={subscription}
                      />
                  }
                  {
                    !subscription.locked &&
                      <SubscriptionPauseResume
                          handleCancel={cancelSubscription}
                          handleReactivate={reactivateSubscription}
                          handlePaymentInfo={handlePaymentInfo}
                          subscription={subscription}
                      />
                  }
                </div>
              }>
                { product &&
                  <Button
                    size="medium"
                    className={classes.colorDefault}
                    onClick={_=> navigateToSubscription(subscription.id)}
                    >
                      <ArrowBack className={classes.leftIcon} />
                      <FormattedMessage id="back.dashboard" />
                  </Button>
                }
              </PageHeader>
            
              <Grid container spacing={32} direction='row'>
              <RootRef rootRef={this.coloredBackgroundDivRef}>
                <Grid item lg={4} md={5} sm={12} classes={{item: classes.currentPlan}} >
                  <Typography variant="h5" className={classNames(classes.colorDefault, classes.subTitle)} >
                      <FormattedMessage id='update.subscription.page.plan.label' />  
                  </Typography> 
                  <SubscriptionCard
                    subscription={subscription}
                    actions={
                      product && !subscription.plan.isFree &&
                          <ChangeSubscription
                            subscription={subscription}
                            product={product}
                            handleUpdatePlan ={this.handleSwitchOnSubscriptionPlanPeriod} 
                            handlePaymentInfo={handlePaymentInfo}
                            handleCancel={cancelSubscription}
                            handleReactivate={reactivateSubscription}
                            handleCurrentPlanUpdate={handleCurrentPlanUpdate}
                            isBothPeriodicityAvailable={isBothPeriodicityAvailable}
                          />
                    }
                  />
                </Grid>
                </RootRef>
                <Grid item lg={8} md={7} sm={12}>
                  {otherPlans && otherPlans.length !== 0 && 
                    <ChangeSubscriptionPlan 
                      subscription={subscription} 
                      otherPlans={otherPlans}
                      handleSwitchOnPeriod={this.handleSwitchOnPeriod}
                      switchOnMonthlyPlans={switchOnMonthlyPlans}
                      isBothPeriodicityAvailable={isBothPeriodicityAvailable}
                      planTargetPeriodicity={planTargetPeriodicity}
                      handlePlanChangeAndPaymentInfo={ plan => handlePlanChange({
                        plan,
                        licencesCount: subscription.licencesCount
                      })}
                    />
                  }
                </Grid>
              </Grid>
            </div>
          )
          : (
            <Loader />
          )}
      </DashboardLayout>
    )
  }

  componentWillMount() {
    const { loadSubscription } = this.props;
    loadSubscription();
  }

  componentWillReceiveProps({ subscriptionId, loadSubscription, handleLockedSubscription }) {
    if (subscriptionId !== this.props.subscriptionId) {
        loadSubscription();
    }
    handleLockedSubscription();
  }
}

UpdateSubscriptionPage.propTypes = {
  intl: intlShape,
  loadSubscription: PropTypes.func.isRequired,
  subscription: PropTypes.object,
  product: PropTypes.object,
  account: PropTypes.object,
  updateSubscription: PropTypes.func.isRequired,
  handlePaymentInfo: PropTypes.func.isRequired,
  cancelSubscription: PropTypes.func.isRequired,
  reactivateSubscription: PropTypes.func.isRequired,
  navigateToSubscription: PropTypes.func.isRequired,
}

export default injectIntl(
      withStyles(styles) (
        withNavigation(UpdateSubscriptionPage)
      )
    );


// WEBPACK FOOTER //
// ./src/components/subscriptions/UpdateSubscriptionPage.js